/**
 * Intelligence Normalization Layer
 *
 * Decouples frontend components from backend field names.
 * Transforms raw API deep-tier data into a stable frontend structure.
 * All fields default to null or empty arrays — no undefined allowed.
 */

import type { WalletIntelData, LiquidityIntelData, ApprovalIntelData, ContractIntelData, HolderIntelData, ProviderPanelData, FundingAttributionData, LockerIntelData, SocialIntelData } from "../components/intel/index.js";

/* ------------------------------------------------------------------ */
/* Stable frontend interface — consumers depend on THESE names only    */
/* ------------------------------------------------------------------ */

export interface NormalizedIntel {
  /** Safety score on 0-10 scale (inverted from risk) */
  safetyScore: number;
  /** Analysis confidence 0-100 */
  analysisConfidence: number;
  /** Confidence level label */
  analysisConfidenceLevel: string | null;

  /** Deployer / creator risk intelligence */
  creatorRisk: WalletIntelData | null;
  /** Deployer funding source attribution */
  creatorFunding: FundingAttributionData | null;
  /** Liquidity lock and exit risk */
  liquiditySafety: LiquidityIntelData | null;
  /** Liquidity locker details */
  liquidityLocker: LockerIntelData | null;
  /** Holder distribution risk */
  holderRisk: HolderIntelData | null;
  /** Contract safety analysis */
  contractSafety: ContractIntelData | null;
  /** Contract approval/control risk */
  contractApproval: ApprovalIntelData | null;
  /** Social presence and trust */
  socialTrust: SocialIntelData | null;

  /** Provider-level data quality / forensic metadata */
  eliteThreat: ProviderPanelData | null;

  /** AI-generated analysis summary */
  verdictSummary: string | null;
  /** Positive findings */
  positives: string[];
  /** Risk findings */
  risks: string[];
  /** Active signals */
  signals: Array<{ type: string; severity: string; message: string }>;

  /** Analysis completion status */
  analysisStatus: string | null;

  /** Token display name (e.g. "USD Coin") */
  tokenName: string | null;
  /** Token ticker symbol (e.g. "USDC") */
  tokenSymbol: string | null;

  /** Whether this token is in a verified-safe database */
  verifiedSafe: boolean;
  /** Source/reason for the verified-safe designation */
  verifiedSafeSource: string | null;

  /** Original deep data for any unmapped field access */
  raw: Record<string, unknown> | null;
}

/* ------------------------------------------------------------------ */
/* Well-known token metadata (frontend fallback when API returns null)  */
/* ------------------------------------------------------------------ */

interface KnownToken { name: string; symbol: string }

const KNOWN_TOKENS: Record<string, Record<string, KnownToken>> = {
  eth: {
    "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48": { name: "USD Coin", symbol: "USDC" },
    "0xdac17f958d2ee523a2206206994597c13d831ec7": { name: "Tether USD", symbol: "USDT" },
    "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2": { name: "Wrapped Ether", symbol: "WETH" },
    "0x6b175474e89094c44da98b954eedeac495271d0f": { name: "Dai Stablecoin", symbol: "DAI" },
    "0x2260fac5e5542a773aa44fbcfedf7c193bc2c599": { name: "Wrapped BTC", symbol: "WBTC" },
    "0x1f9840a85d5af5bf1d1762f925bdaddc4201f984": { name: "Uniswap", symbol: "UNI" },
    "0x514910771af9ca656af840dff83e8264ecf986ca": { name: "Chainlink", symbol: "LINK" },
    "0x7fc66500c84a76ad7e9c93437bfc5ac33e2ddae9": { name: "Aave", symbol: "AAVE" },
  },
  bnb: {
    "0xe9e7cea3dedca5984780bafc599bd69add087d56": { name: "Binance-Peg BUSD", symbol: "BUSD" },
    "0x55d398326f99059ff775485246999027b3197955": { name: "Binance-Peg BSC-USD", symbol: "USDT" },
    "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d": { name: "Binance-Peg USD Coin", symbol: "USDC" },
    "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c": { name: "Wrapped BNB", symbol: "WBNB" },
  },
  base: {
    "0x833589fcd6edb6e08f4c7c32d4f71b54bda02913": { name: "USD Coin", symbol: "USDC" },
    "0x4200000000000000000000000000000000000006": { name: "Wrapped Ether", symbol: "WETH" },
  },
};

/**
 * Look up well-known token metadata by chain + contract address.
 * Returns null if not in the known list.
 */
export function lookupKnownToken(chain: string, contract: string): KnownToken | null {
  return KNOWN_TOKENS[chain]?.[contract.toLowerCase()] ?? null;
}

/* ------------------------------------------------------------------ */
/* Normalizer                                                          */
/* ------------------------------------------------------------------ */

/**
 * Transform raw deep-tier API data into stable frontend structure.
 *
 * Accepts the deep data object (IntelDeepData from API) or any
 * Record<string, unknown> shape. Returns NormalizedIntel with every
 * field guaranteed non-undefined.
 */
export function normalizeIntel(rawIntel: Record<string, unknown> | null | undefined): NormalizedIntel {
  if (!rawIntel) {
    return emptyIntel();
  }

  const r = rawIntel as Record<string, any>;

  return {
    // Scores
    safetyScore: typeof r.score === "number" ? r.score : 0,
    analysisConfidence: r.confidenceScore ?? r.confidence ?? 0,
    analysisConfidenceLevel: r.confidenceLevel ?? null,

    // Domain intelligence blocks — map backend names → stable frontend names
    creatorRisk: r.walletIntel ?? null,
    creatorFunding: r.fundingAttribution ?? null,
    liquiditySafety: r.liquidityIntel ?? null,
    liquidityLocker: r.lockerIntel ?? null,
    holderRisk: r.holderIntel ?? null,
    contractSafety: r.contractIntel ?? null,
    contractApproval: r.approvalIntel ?? null,
    socialTrust: r.socialIntel ?? null,

    // Elite threat / provider metadata
    eliteThreat: r.eliteIntel ?? r.eliteThreat ?? r.confidenceFactors ?? null,

    // Verdict content
    verdictSummary: r.summary ?? null,
    positives: Array.isArray(r.positives) ? r.positives : [],
    risks: Array.isArray(r.risks) ? r.risks : [],
    signals: Array.isArray(r.signals) ? r.signals : [],

    // Status
    analysisStatus: r.analysisStatus ?? null,

    // Token metadata — walk multiple possible locations, fall back to known-token list
    tokenName: r.project?.name ?? r.token?.name ?? r.name
      ?? lookupKnownToken(r.chain ?? "", r.token ?? r.contract ?? "")?.name ?? null,
    tokenSymbol: r.project?.symbol ?? r.project?.ticker ?? r.token?.symbol ?? r.symbol
      ?? lookupKnownToken(r.chain ?? "", r.token ?? r.contract ?? "")?.symbol ?? null,

    // Verified safe token flag
    verifiedSafe: !!r.verifiedSafe,
    verifiedSafeSource: typeof r.verifiedSafeSource === 'string' ? r.verifiedSafeSource : null,

    // Preserve raw for unmapped access
    raw: rawIntel as Record<string, unknown>,
  };
}

/* ------------------------------------------------------------------ */
/* Empty fallback — all null / empty, zero undefined                   */
/* ------------------------------------------------------------------ */

function emptyIntel(): NormalizedIntel {
  return {
    safetyScore: 0,
    analysisConfidence: 0,
    analysisConfidenceLevel: null,
    creatorRisk: null,
    creatorFunding: null,
    liquiditySafety: null,
    liquidityLocker: null,
    holderRisk: null,
    contractSafety: null,
    contractApproval: null,
    socialTrust: null,
    eliteThreat: null,
    verdictSummary: null,
    positives: [],
    risks: [],
    signals: [],
    analysisStatus: null,
    tokenName: null,
    tokenSymbol: null,
    verifiedSafe: false,
    verifiedSafeSource: null,
    raw: null,
  };
}
